// @ts-strict-ignore
import {AfterViewInit, Component, effect, inject, input, output} from "@angular/core";
import {configureAssessmentProviders} from "../services/assessable.configuration";
import {AssessableUiService} from "../services/assessable-ui.service";
import {Translations} from "@translations";
import {AssessablePanelMode, AssessablePanelType} from "@common/components/assessment/utils";
import {Assessment, AssessmentItem, ChecklistItemType, UserRecordingAssessment} from "@models";
import {SimAssessmentScoreService, TrackByMethods} from "@services";
import {AssessmentContextService} from "../services/assessment-context.service";
import {AssessmentAnswerService} from "../services/assessment-answer.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {AssessableProxyService} from "../services/assessable-proxy.service";
import {tap} from "rxjs/operators";

@UntilDestroy()
@Component({
	selector: "sim-assessment-edit-panel",
	templateUrl: "./assessment-edit-panel.component.html",
	styleUrl: "./assessment-edit-panel.component.scss",
	providers: [...configureAssessmentProviders()],
	standalone: false,
})
export class AssessmentEditPanelComponent implements AfterViewInit {
	// Enums
	protected readonly T = Translations;
	protected readonly AssessablePanelType = AssessablePanelType;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly ChecklistItemType = ChecklistItemType;
	protected readonly TrackByMethods = TrackByMethods;

	// I/O
	public assessable = input.required<Assessment | UserRecordingAssessment>();
	public isInArchive = input(false);
	public hideScore = input(false);

	public assessmentChanged = output<Assessment>();
	public onJumpToItem = output<number>();

	readonly #initContextEffect = effect((onCleanup) => {
		this.store.setMode(AssessablePanelMode.Edit);

		const value = this.assessable();
		this.contextService.initialize(value, value.competenceCategoryIds);
		this.contextService.setAssessment(value);
		this._answerService.initialize(this.assessable);

		const sub = this.contextService.$savingPipeline.pipe(untilDestroyed(this)).subscribe();
		onCleanup(() => sub.unsubscribe());
	});

	// Injections
	public proxyService = inject(AssessableProxyService);
	protected contextService = inject(AssessmentContextService);
	protected store: AssessableUiService<AssessmentItem> = inject(AssessableUiService);
	private _answerService = inject(AssessmentAnswerService);
	private _simAssessmentScoreService = inject(SimAssessmentScoreService);

	public groupExpanded = this.store.groupExpanded;
	public itemExpanded = this.store.itemExpanded;
	public hasAnyImportedItems = this.contextService.hasAnyImportedItems;
	public errorIds = this.contextService.errorIds;
	public isStateValid = this.contextService.isFormValid;

	public readonly $changedAssessment = this.contextService.$assessable.pipe(tap((assessment) => this.assessmentChanged.emit(assessment)));

	public ngAfterViewInit() {
		this.store.expandFirstGroupAndItem();
	}

	public getAssessment(): Assessment {
		return this.contextService.getAssessable();
	}

	public getGroupScore(groupId: string): string {
		if (this.store.mode() === AssessablePanelMode.Edit) {
			return "-";
		}
		const assessment = this.contextService.getAssessable();
		const group = assessment.assessableGroups.find((group) => group.groupId === groupId);
		const groupScore = this._simAssessmentScoreService.getGroupScore(group, assessment);

		if (isNaN(groupScore)) {
			return "-";
		}
		return `${groupScore}`;
	}

	public getMaxGroupScore(groupId: string): string {
		const assessment = this.contextService.getAssessable();
		const group = assessment.assessableGroups.find((group) => group.groupId === groupId);
		if (!group) {
			return "-";
		}

		const groupMaxScore = this._simAssessmentScoreService.getGroupMaxScore(group);

		if (isNaN(groupMaxScore)) {
			return "-";
		}
		return `${groupMaxScore}`;
	}

	public scrollAndEditGroupAndItem(groupId: string, itemId: string) {
		this.store.scrollAndEditItem(groupId, itemId);
	}

	public async saveAssessable() {
		const assessment = this.contextService.getAssessable();
		await this.contextService.saveAssessable(assessment);
	}
}
